import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
    ALL_GRANTS,
    CookieConsentService,
    NO_GRANTS,
    REQUIRES_RELOAD_TO_WITHDRAW,
    TrackerGrants,
    TrackerKey,
    TRACKER_KEYS
} from '../../services/cookie-consent.service';

type ConsentView = 'choice' | 'manage' | 'unavailable';

/** `gate` is shown before any decision exists and cannot be dismissed; `settings` can. */
type ConsentMode = 'gate' | 'settings';

type CategoryKey = 'analytics' | 'customerSuccess';

interface TrackerCopy {
    category: CategoryKey;
    nameKey: string;
    descriptionKey: string;
}

interface TrackerOption extends TrackerCopy {
    id: TrackerKey;
}

interface ConsentCategory {
    titleKey: string;
    trackers: readonly TrackerOption[];
}

/**
 * What the user is told about each optional service, and which category it
 * sits in. A host app that does not include a tracker (see the @Input flags
 * below) simply never shows its row — the copy stays here so it does not need
 * to be duplicated per app.
 */
const TRACKER_COPY: Record<TrackerKey, TrackerCopy> = {
    mixpanel: {
        category: 'analytics',
        nameKey: 'Mixpanel',
        descriptionKey:
            'Helps us understand feature usage and improve the product. If enabled, Mixpanel may receive your account identifier, name, email or username, company, role and actions taken in the app, and may store a device/account identifier.'
    },
    site24x7: {
        category: 'analytics',
        nameKey: 'Site24x7',
        descriptionKey:
            'Measures application performance and reliability. If enabled, Site24x7 may store a persistent visitor/session identifier on this domain.'
    },
    productfruits: {
        category: 'customerSuccess',
        nameKey: 'ProductFruits',
        descriptionKey:
            'Provides in-product guidance and product analytics. If enabled, ProductFruits may receive your account, contact, role, company and usage information.'
    },
    churnzero: {
        category: 'customerSuccess',
        nameKey: 'ChurnZero',
        descriptionKey:
            'Supports customer-success and product-engagement activities. If enabled, ChurnZero may receive account and user details, including email, role and country, and may set cookies on its own domain.'
    }
};

@Component({
    selector: 'app-cookie-consent-modal',
    templateUrl: './cookie-consent-modal.component.html',
    styleUrls: ['./cookie-consent-modal.component.scss'],
    standalone: false
})
export class CookieConsentModalComponent
    implements OnInit, OnChanges, OnDestroy
{
    // ChurnZero is only wired up (and only injected by TrackingScriptsService)
    // in the customer app — admin does not use it.
    @Input() includeChurnZero = false;

    // Both apps carry Mixpanel and ProductFruits, so these default on; a host
    // that adds neither can opt out explicitly.
    @Input() includeMixpanel = true;
    @Input() includeProductFruits = true;

    /** The in-app policy page. Opened in a new tab so reading it never destroys a half-made choice here. */
    @Input() policyPath = '/cookie-policy';

    private mode: ConsentMode = 'gate';
    showModal = false;
    view: ConsentView = 'choice';
    cameFromChoice = false;
    draft: TrackerGrants = NO_GRANTS;
    /** Stable list — must not be a getter that allocates each CD (breaks *ngFor clicks). */
    categories: ConsentCategory[] = [];
    /** The grants as stored when the modal opened, so a revocation can be detected. */
    private stored: TrackerGrants = NO_GRANTS;

    private reopenSubscription: Subscription;

    constructor(
        private cookieConsentService: CookieConsentService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // The policy page itself is the one place the gate must not auto-open:
        // it is meant to be read calmly, on its own, often in the new tab the
        // modal's own "Cookie Policy" link opens. Its "Manage preferences"
        // button still opens this modal — just via requestReopen(), not here.
        this.refreshCategories();
        const onPolicyPage = window.location.pathname === this.policyPath;
        this.showModal =
            !onPolicyPage && !this.cookieConsentService.hasDecision();
        if (this.showModal) this.openFresh('gate');

        this.reopenSubscription =
            this.cookieConsentService.reopenRequest$.subscribe((mode) =>
                this.openFresh(mode)
            );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['includeChurnZero'] ||
            changes['includeMixpanel'] ||
            changes['includeProductFruits']
        ) {
            this.refreshCategories();
        }
    }

    ngOnDestroy(): void {
        this.reopenSubscription?.unsubscribe();
    }

    /** Only the account-menu and in-app policy entry points may be dismissed. */
    get dismissable(): boolean {
        return this.mode === 'settings';
    }

    /**
     * Rebuild the tracker list when host @Inputs change. Kept as a stored
     * array (not a getter) so *ngFor rows stay stable across change detection;
     * a new array every CD cycle was destroying toggle click handlers.
     */
    private refreshCategories(): void {
        const included = new Set<TrackerKey>(
            TRACKER_KEYS.filter((key) => this.isIncluded(key))
        );
        const trackersIn = (category: CategoryKey): TrackerOption[] =>
            TRACKER_KEYS.filter(
                (key) =>
                    included.has(key) && TRACKER_COPY[key].category === category
            ).map((key) => ({ id: key, ...TRACKER_COPY[key] }));

        const categories: ConsentCategory[] = [
            {
                titleKey: 'Analytics / performance',
                trackers: trackersIn('analytics')
            }
        ];
        const customerSuccess = trackersIn('customerSuccess');
        if (customerSuccess.length > 0) {
            categories.push({
                titleKey: 'Customer success / product adoption',
                trackers: customerSuccess
            });
        }
        this.categories = categories;
    }

    private isIncluded(key: TrackerKey): boolean {
        if (key === 'mixpanel') return this.includeMixpanel;
        if (key === 'productfruits') return this.includeProductFruits;
        if (key === 'churnzero') return this.includeChurnZero;
        return true; // site24x7 runs in both apps today
    }

    acceptAll(): void {
        this.commit(ALL_GRANTS);
    }

    rejectAll(): void {
        this.commit(NO_GRANTS);
    }

    saveSelection(): void {
        this.commit(this.draft);
    }

    manage(): void {
        this.cameFromChoice = true;
        this.view = 'manage';
    }

    backToChoice(): void {
        this.view = 'choice';
    }

    trackByTrackerId(_index: number, tracker: TrackerOption): TrackerKey {
        return tracker.id;
    }

    /** Switch control: stop row bubbling so a single click cannot flip twice. */
    onToggleClick(event: Event, tracker: TrackerKey): void {
        event.preventDefault();
        event.stopPropagation();
        this.toggleTracker(tracker, !this.draft[tracker]);
    }

    toggleTracker(tracker: TrackerKey, enabled: boolean): void {
        this.draft = { ...this.draft, [tracker]: enabled };
        this.changeDetectorRef.detectChanges();
    }

    dismiss(): void {
        if (!this.dismissable) return;
        this.showModal = false;
    }

    /**
     * Leaves the unavailable state without recording anything. Do not block
     * access solely because the preference cannot be persisted — the app is
     * released with every optional service still denied, which needs no extra
     * code, because an absent record already resolves to all-denied.
     */
    continueWithoutRecord(): void {
        this.showModal = false;
    }

    private openFresh(mode: ConsentMode): void {
        this.mode = mode;
        this.cameFromChoice = false;
        this.stored = this.cookieConsentService.getGrants();
        // Clone so draft edits never mutate the snapshot used for reload checks.
        this.draft = { ...this.stored };
        this.view = this.resolveView();
        this.showModal = true;
        this.changeDetectorRef.detectChanges();
    }

    /**
     * The single write path. Nothing closes until the record is actually
     * stored, which is what keeps the sign-in gate honest.
     */
    private commit(selection: TrackerGrants): void {
        const reloadNeeded = this.revokesLoadedScript(selection);
        if (!this.cookieConsentService.save(selection)) {
            // The write did not stick, so cookies are blocked despite the earlier probe.
            this.view = 'unavailable';
            return;
        }
        this.showModal = false;
        if (reloadNeeded) window.location.reload();
    }

    /**
     * Only a tracker that cannot be un-loaded needs a fresh document to stop.
     * Granting never does — the loader/init call runs on the spot — and
     * withdrawing Mixpanel does not either, because its own opt-out takes
     * effect immediately. Before any tracker has loaded (the sign-in gate)
     * nothing can be withdrawn, so the gate never reloads.
     */
    private revokesLoadedScript(selection: TrackerGrants): boolean {
        return REQUIRES_RELOAD_TO_WITHDRAW.some(
            (key) => this.stored[key] && !selection[key]
        );
    }

    private resolveView(): ConsentView {
        if (!this.cookieConsentService.areCookiesUsable()) return 'unavailable';
        // No record means ask; a record means adjust. Independent of `mode`.
        return this.cookieConsentService.hasDecision() ? 'manage' : 'choice';
    }
}
